import React, { useEffect, useState } from 'react';
import { Sparkles, Instagram, TrendingUp, Users, Trophy, CheckCircle2, AlertCircle, ExternalLink, RefreshCw, Link as LinkIcon, Trash2, QrCode, Download, Target, Plus, MessageSquare } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { refreshSession } from '../lib/session';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('advecti_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const BloomAttributionTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [media, setMedia] = useState([]);
  const [savingPin, setSavingPin] = useState(false);
  const [error, setError] = useState('');
  const [activeIg, setActiveIg] = useState('');
  const [entries, setEntries] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [checking, setChecking] = useState(false);

  const authedGet = async (path) => {
    let r = await fetch(`${BACKEND_URL}${path}`, { headers: authHeaders() });
    if (r.status === 401 && await refreshSession()) {
      r = await fetch(`${BACKEND_URL}${path}`, { headers: authHeaders() });
    }
    return r;
  };

  const fetchEntries = async (igId = activeIg) => {
    try {
      const qs = igId ? `?ig_user_id=${encodeURIComponent(igId)}` : '';
      const r = await authedGet(`/api/bloom/entries${qs}`);
      if (r.ok) setEntries((await r.json()).entries || []);
    } catch (e) { console.error(e); }
  };

  const fetchAttribution = async (igId = activeIg) => {
    setLoading(true);
    setLoadError('');
    try {
      const qs = igId ? `?ig_user_id=${encodeURIComponent(igId)}` : '';
      const r = await authedGet(`/api/bloom/attribution${qs}`);
      if (r.ok) {
        const d = await r.json();
        setData(d);
        // Default to the first connected account so a two-brand campaign always
        // has an explicit brand selected.
        if (!igId && (d.connections || []).length > 1) setActiveIg(d.connections[0].ig_user_id || '');
        // Server fell back to another brand (stale selection) — follow it.
        if (igId && d.ig_user_id && d.ig_user_id !== igId) setActiveIg(d.ig_user_id);
      } else {
        setLoadError(r.status === 401 ? 'Your session expired. Please sign in again.' : `Could not load your Instagram connection (HTTP ${r.status}).`);
      }
    } catch (e) {
      console.error(e);
      setLoadError('Could not reach Advecti. Check your connection and retry.');
    }
    await fetchEntries(igId);
    setLoading(false);
  };

  // Refresh = ask Instagram for new comments right now, then reload the numbers.
  const checkNow = async () => {
    setChecking(true);
    try {
      const qs = activeIg ? `?ig_user_id=${encodeURIComponent(activeIg)}` : '';
      let r = await fetch(`${BACKEND_URL}/api/bloom/sweep${qs}`, { method: 'POST', headers: authHeaders() });
      if (r.status === 401 && await refreshSession()) {
        r = await fetch(`${BACKEND_URL}/api/bloom/sweep${qs}`, { method: 'POST', headers: authHeaders() });
      }
    } catch (e) { console.error(e); }
    await fetchAttribution();
    setChecking(false);
  };

  useEffect(() => { fetchAttribution(); }, []);

  // The Instagram callback is server-side and redirects back here with a result.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const igError = params.get('ig_error');
    const connected = params.get('ig_connected');
    if (igError) setError(igError);
    if (connected) fetchAttribution();
    if (igError || connected) {
      params.delete('ig_error');
      params.delete('ig_connected');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  const startConnect = async () => {
    setConnecting(true);
    setError('');
    try {
      let r = await fetch(`${BACKEND_URL}/api/instagram/oauth/start`, { headers: authHeaders() });
      if (r.status === 401) {
        // Stale tab: renew silently rather than sending the advertiser to Instagram
        // with a dead session and stranding them on an error page.
        const fresh = await refreshSession();
        if (fresh) {
          r = await fetch(`${BACKEND_URL}/api/instagram/oauth/start`, { headers: authHeaders() });
        }
      }
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to start Instagram connection');
      }
      const { url } = await r.json();
      if (!url) throw new Error('Instagram connection is not configured yet. Contact support.');
      window.location.href = url;
    } catch (e) {
      setError(e.message);
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    const who = data?.connection?.ig_username ? `@${data.connection.ig_username}` : 'this account';
    if (!window.confirm(`Disconnect ${who}? Your existing follower history and entries will be preserved, but new attribution will stop until you reconnect.`)) return;
    const qs = activeIg ? `?ig_user_id=${encodeURIComponent(activeIg)}` : '';
    await fetch(`${BACKEND_URL}/api/bloom/disconnect${qs}`, { method: 'POST', headers: authHeaders() });
    setActiveIg('');
    fetchAttribution('');
  };

  const openPinnedPicker = async () => {
    setPickerOpen(true);
    try {
      const qs = activeIg ? `?ig_user_id=${encodeURIComponent(activeIg)}` : '';
      const r = await fetch(`${BACKEND_URL}/api/bloom/media${qs}`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setMedia(d.media || []);
      }
    } catch (e) { console.error(e); }
  };

  const setPinned = async (media_id) => {
    setSavingPin(true);
    try {
      await fetch(`${BACKEND_URL}/api/bloom/pinned`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ media_id, ig_user_id: activeIg }),
      });
      setPickerOpen(false);
      fetchAttribution();
    } catch (e) { console.error(e); }
    setSavingPin(false);
  };

  if (loading && !data) {
    return <div className="text-navy/60 py-12 text-center" data-testid="bloom-loading">Loading Bloom Attribution…</div>;
  }

  const connections = data?.connections || [];
  const connection = data?.connection || connections[0] || null;
  // Only a successful load with zero accounts means "not connected". A failed
  // request must never send an already-connected advertiser back to Connect.
  const isConnected = !!connection;
  const showConnect = !!data && !loadError && connections.length === 0;
  const hasPinned = !!connection?.pinned_media_id;
  const reported = connection?.pinned_comments_reported;
  const visible = connection?.pinned_comments_visible;
  const hiddenComments = hasPinned && Number.isFinite(reported) && reported > (visible || 0) ? reported - (visible || 0) : 0;

  return (
    <div data-testid="bloom-attribution-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-pink-100 rounded-2xl p-3">
            <Sparkles className="text-pink-600" size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-navy">Bloom Attribution</h2>
            <p className="text-navy/60 text-sm">Real followers, real attribution — no revenue sharing required.</p>
          </div>
        </div>
        <Link to="/bloom/rules" target="_blank" className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1" data-testid="bloom-rules-link">
          Official rules <ExternalLink size={12} />
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {loadError && !isConnected && (
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-6 text-center" data-testid="bloom-load-error-card">
          <AlertCircle className="text-amber-600 mx-auto mb-3" size={36} />
          <h3 className="text-lg font-bold text-navy mb-2">Couldn&apos;t load Bloom Attribution</h3>
          <p className="text-navy/70 text-sm max-w-md mx-auto mb-5">{loadError}</p>
          <button
            onClick={() => fetchAttribution()}
            className="px-5 py-2.5 bg-navy text-white rounded-xl font-semibold hover:bg-navy/90 inline-flex items-center gap-2"
            data-testid="bloom-retry-btn"
          >
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      )}

      {/* Step 1: connect */}
      {showConnect && (
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-6 text-center" data-testid="bloom-connect-card">
          <Instagram className="text-pink-600 mx-auto mb-3" size={40} />
          <h3 className="text-xl font-bold text-navy mb-2">Connect your Instagram Business Account</h3>
          <p className="text-navy/70 text-sm max-w-md mx-auto mb-6">
            Bloom Attribution uses the Instagram Graph API to auto-verify sweepstakes
            entries and track daily follower growth. You&apos;ll need an <strong>Instagram
            Business or Creator account</strong> linked to a <strong>Facebook Page</strong>.
          </p>
          <button
            onClick={startConnect}
            disabled={connecting}
            className="px-6 py-3 bg-pink-600 text-white rounded-xl font-semibold hover:bg-pink-700 transition disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="bloom-connect-btn"
          >
            <Instagram size={18} />
            {connecting ? 'Redirecting…' : 'Connect Instagram'}
          </button>
          <p className="text-navy/50 text-xs mt-4">
            We request read-only access to your follower count and public post comments.
            You can disconnect at any time.
          </p>
        </div>
      )}

      {/* Brand switcher — a wrap can carry one brand per side, each with its own
          Instagram account, giveaway post and entries. */}
      {isConnected && (
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="bloom-account-switcher">
          {connections.map((c) => {
            const active = (activeIg || connections[0]?.ig_user_id) === c.ig_user_id;
            return (
              <button
                key={c.ig_user_id}
                onClick={() => { setActiveIg(c.ig_user_id); fetchAttribution(c.ig_user_id); }}
                className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${active ? 'bg-navy text-white' : 'bg-white text-navy/70 hover:bg-cream'}`}
                data-testid={`bloom-account-pill-${c.ig_username || c.ig_user_id}`}
              >
                <Instagram size={14} />
                @{c.ig_username || c.ig_user_id}
              </button>
            );
          })}
          <button
            onClick={startConnect}
            disabled={connecting}
            className="px-4 py-2 rounded-full text-sm font-semibold text-pink-700 border border-dashed border-pink-300 hover:bg-pink-50 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="bloom-connect-another-btn"
          >
            <Plus size={14} /> {connecting ? 'Redirecting…' : 'Connect another account'}
          </button>
        </div>
      )}

      {/* Connected — show account card + pinned selector */}
      {isConnected && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6" data-testid="bloom-connected-card">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {connection.ig_profile_pic ? (
                <img src={connection.ig_profile_pic} alt="" className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-pink-100 flex items-center justify-center">
                  <Instagram className="text-pink-600" size={26} />
                </div>
              )}
              <div>
                <div className="font-bold text-navy text-lg">@{connection.ig_username || '(unknown)'}</div>
                <div className="text-navy/60 text-sm">
                  {(connection.followers_count || 0).toLocaleString()} followers · connected {new Date(connection.connected_at).toLocaleDateString()}
                </div>
                {connection.followers_checked_at && (
                  <div className="text-navy/50 text-xs" data-testid="bloom-followers-checked">
                    Follower count from Instagram at {new Date(connection.followers_checked_at).toLocaleTimeString()} · refreshes every 10 min
                  </div>
                )}
                {connection.subscribed_at ? (
                  <div className="text-green-700 text-xs mt-1 inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> Comment webhook active
                  </div>
                ) : (
                  <div className="text-amber-700 text-xs mt-1 inline-flex items-center gap-1">
                    <AlertCircle size={12} /> Webhook pending Meta app review
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={checkNow}
                disabled={checking}
                className="px-3 py-2 text-navy/70 hover:bg-cream rounded-lg text-sm inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="bloom-refresh-btn"
              >
                <RefreshCw size={14} className={checking ? 'animate-spin' : ''} /> {checking ? 'Checking Instagram…' : 'Check Instagram now'}
              </button>
              <button
                onClick={disconnect}
                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm inline-flex items-center gap-1"
                data-testid="bloom-disconnect-btn"
              >
                <Trash2 size={14} /> Disconnect
              </button>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-navy/10">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-navy/60 text-xs uppercase font-semibold tracking-wider">Pinned Post</div>
                <div className="text-navy font-semibold text-sm mt-0.5">
                  {hasPinned ? `Media ID: ${connection.pinned_media_id}` : 'No pinned post selected yet'}
                </div>
                <div className="text-navy/50 text-xs mt-0.5">
                  Bloom entries are counted from comments on this post. Update whenever you change your IG pinned post.
                </div>
              </div>
              <button
                onClick={openPinnedPicker}
                className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy/90 inline-flex items-center gap-1"
                data-testid="bloom-pinned-picker-btn"
              >
                <LinkIcon size={14} /> {hasPinned ? 'Change pinned post' : 'Select pinned post'}
              </button>
            </div>
            {hasPinned && connection.last_sweep_at && (
              <div className="text-navy/50 text-xs mt-3" data-testid="bloom-sweep-status">
                Instagram reports <strong>{reported ?? '—'}</strong> comment{reported === 1 ? '' : 's'} on this post ·
                Bloom can read <strong>{visible ?? 0}</strong> · last checked {new Date(connection.last_sweep_at).toLocaleTimeString()} (auto-checks every 10 min)
              </div>
            )}
            {hiddenComments > 0 && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="bloom-hidden-comments-notice">
                <div className="font-semibold inline-flex items-center gap-1.5 mb-1">
                  <AlertCircle size={15} /> {hiddenComments} comment{hiddenComments === 1 ? ' is' : 's are'} on this post that Meta isn&apos;t sharing with Bloom yet
                </div>
                <p className="text-amber-900/80">
                  Until Meta App Review grants Advanced Access, Instagram only returns comments written by
                  accounts listed as <strong>Instagram Testers</strong> on the Advecti app — everyone else&apos;s
                  comments are counted but hidden. Nothing is lost: the moment Advanced Access is granted,
                  Bloom&apos;s automatic check imports every past comment on this post as entries.
                  To test today, add the commenting account as an Instagram Tester (Meta App Dashboard → App Roles → Roles)
                  and have them accept the invite in Instagram → Settings → Website permissions → Apps and websites → Tester invites.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stat cards */}
      {isConnected && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            icon={<Users size={18} />}
            label="Current followers"
            value={(connection.followers_count || 0).toLocaleString()}
            testid="bloom-stat-followers"
          />
          <StatCard
            icon={<TrendingUp size={18} />}
            label="Followers gained"
            value={data?.followers_series?.length ? Math.max(0, (data.followers_series[data.followers_series.length - 1]?.followers || 0) - (data.followers_series[0]?.followers || 0)).toLocaleString() : '—'}
            testid="bloom-stat-gained"
          />
          <StatCard
            icon={<QrCode size={18} />}
            label={`QR scans · cycle ${data?.current_cycle_index || 0}`}
            value={`${(data?.scans_current_cycle || 0).toLocaleString()} / ${(data?.scans_total || 0).toLocaleString()}`}
            hint={`${(data?.unique_scans_current_cycle || 0).toLocaleString()} unique · all-time ${(data?.unique_scans_total || 0).toLocaleString()}`}
            testid="bloom-stat-scans"
          />
          <StatCard
            icon={<Sparkles size={18} />}
            label={`Entries · cycle ${data?.current_cycle_index || 0}`}
            value={(data?.current_cycle_entries || 0).toLocaleString()}
            testid="bloom-stat-entries"
          />
          <StatCard
            icon={<Trophy size={18} />}
            label="Drawings remaining"
            value={`${data?.drawings_remaining || 0} / ${data?.campaign_duration_months || 0}`}
            testid="bloom-stat-drawings"
          />
        </div>
      )}

      {/* Wrap QR code — always shown when connected so advertiser can print it on their wrap */}
      {isConnected && data?.advertiser_id && (
        <BloomWrapQR
          advertiser_id={data.advertiser_id}
          ig_username={connection.ig_username}
          multi={connections.length > 1}
          prize={connection.prize_description}
        />
      )}

      {/* Giveaway entries — the actual comments that earned entries. Advertisers want
          to see who entered, and Meta App Review requires the comment data we read to
          be visible in the product. */}
      {isConnected && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6" data-testid="bloom-entries-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <div className="flex items-center gap-2">
              <MessageSquare size={18} className="text-pink-600" />
              <h3 className="font-bold text-navy">Giveaway entries</h3>
            </div>
            <span className="text-navy/50 text-xs">
              {entries.length} comment{entries.length === 1 ? '' : 's'}
              {connection.ig_username ? ` on @${connection.ig_username}'s post` : ''}
            </span>
          </div>
          <p className="text-navy/60 text-xs mb-4">
            Every comment on your giveaway post that tags at least one friend becomes an
            entry. Each extra friend tagged in the same comment adds a bonus entry, up to 5.
          </p>
          {entries.length === 0 ? (
            <p className="text-navy/60 text-sm" data-testid="bloom-entries-empty">
              No entries yet. Once people scan your wrap and comment on your giveaway post,
              they&apos;ll appear here.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[560px]" data-testid="bloom-entries-table">
                <thead className="text-navy/60 text-xs uppercase">
                  <tr className="border-b border-navy/10">
                    <th className="text-left py-2">Entrant</th>
                    <th className="text-left py-2">Comment</th>
                    <th className="text-left py-2">Tagged</th>
                    <th className="text-left py-2">Entries</th>
                    <th className="text-left py-2">Cycle</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.comment_id} className="border-b border-navy/5 text-navy/80" data-testid={`bloom-entry-row-${e.comment_id}`}>
                      <td className="py-2 whitespace-nowrap">
                        <a
                          href={`https://instagram.com/${e.entrant_handle}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-pink-600 hover:underline"
                        >
                          @{e.entrant_handle}
                        </a>
                      </td>
                      <td className="py-2 max-w-[320px] truncate" title={e.comment_text}>{e.comment_text}</td>
                      <td className="py-2 text-xs">{(e.tags || []).map((t) => `@${t}`).join(', ')}</td>
                      <td className="py-2 font-semibold">{e.entries_count}</td>
                      <td className="py-2 text-xs">#{e.cycle_index}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Attribution Conversion Funnel — the killer sales-deck number */}
      {isConnected && (data?.unique_scans_current_cycle > 0 || data?.current_cycle_entries > 0) && (
        <div className="bg-white rounded-2xl shadow-sm border border-pink-100 p-6 mb-6" data-testid="bloom-funnel-card">
          <div className="flex items-center gap-2 mb-1">
            <Target size={18} className="text-pink-600" />
            <h3 className="font-bold text-navy">Attribution Funnel · Cycle {data?.current_cycle_index || 0}</h3>
          </div>
          <p className="text-navy/60 text-xs mb-4">
            The single most powerful stat for offline advertising: what % of people who
            scanned your wrap actually became engaged Instagram commenters. No billboard,
            radio ad, or Meta ad can produce this number.
          </p>
          <div className="grid grid-cols-3 gap-4">
            <FunnelStep
              n={1}
              label="Unique scanners"
              value={(data?.unique_scans_current_cycle || 0).toLocaleString()}
              caption="Real people who tapped your wrap QR"
              testid="bloom-funnel-scans"
            />
            <FunnelStep
              n={2}
              label="Sweepstakes entries"
              value={(data?.current_cycle_entries || 0).toLocaleString()}
              caption="Commented on pinned post with a friend tag"
              testid="bloom-funnel-entries"
            />
            <FunnelStep
              n={3}
              label="Conversion rate"
              value={`${data?.scan_to_entry_conversion_pct || 0}%`}
              caption="Entries ÷ unique scanners"
              testid="bloom-funnel-conversion"
              accent
            />
          </div>
        </div>
      )}

      {/* Follower growth chart */}
      {isConnected && data?.followers_series?.length > 1 && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6" data-testid="bloom-followers-chart">
          <h3 className="font-bold text-navy mb-1">Daily follower growth</h3>
          <p className="text-navy/60 text-xs mb-4">Snapshot taken daily from your connected Instagram Business account.</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.followers_series}>
              <defs>
                <linearGradient id="pinkGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ec4899" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#ec4899" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RechartsTooltip />
              <Area type="monotone" dataKey="followers" stroke="#ec4899" fill="url(#pinkGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Drawings history */}
      {isConnected && (
        <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="bloom-drawings-history">
          <h3 className="font-bold text-navy mb-4">Drawing history</h3>
          {(!data?.drawings || data.drawings.length === 0) ? (
            <p className="text-navy/60 text-sm">
              No drawings yet. Your first winner will be selected 30 days after your
              campaign start date. You&apos;ll receive an email with the winner&apos;s Instagram
              handle to arrange the $100 gift card.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-navy/60 text-xs uppercase font-semibold">
                  <tr className="border-b border-navy/10">
                    <th className="text-left py-2">Cycle</th>
                    <th className="text-left py-2">Window</th>
                    <th className="text-left py-2">Winner</th>
                    <th className="text-left py-2">Entries</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.drawings.map((d) => (
                    <tr key={d.id} className="border-b border-navy/5 text-navy/80" data-testid={`bloom-drawing-row-${d.cycle_index}`}>
                      <td className="py-2">#{d.cycle_index}</td>
                      <td className="py-2 text-xs">{d.cycle_start?.slice(0, 10)} → {d.cycle_end?.slice(0, 10)}</td>
                      <td className="py-2">
                        {d.winner_handle ? (
                          <a href={`https://instagram.com/${d.winner_handle}`} target="_blank" rel="noreferrer" className="text-pink-600 hover:underline">
                            @{d.winner_handle}
                          </a>
                        ) : (
                          <span className="text-navy/40 italic">no entries</span>
                        )}
                      </td>
                      <td className="py-2">{d.total_entries} ({d.total_entrants} unique)</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          d.status === 'paid' ? 'bg-green-100 text-green-700' :
                          d.status === 'no_entries' ? 'bg-navy/10 text-navy/60' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {d.status === 'paid' ? 'Paid' : d.status === 'no_entries' ? 'No entries' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Media picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-[9999] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bloom-picker-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
              <h3 className="font-bold text-navy">Pick your pinned Instagram post</h3>
              <button onClick={() => setPickerOpen(false)} className="text-navy/60 hover:text-navy">✕</button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {media.length === 0 ? (
                <p className="text-navy/60 text-sm">Loading your recent posts…</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {media.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setPinned(m.id)}
                      disabled={savingPin}
                      className="text-left bg-cream rounded-xl overflow-hidden border-2 border-transparent hover:border-pink-500 transition disabled:opacity-50"
                      data-testid={`bloom-media-pick-${m.id}`}
                    >
                      {(m.thumbnail_url || m.media_url) && (
                        <img src={m.thumbnail_url || m.media_url} alt="" className="w-full h-40 object-cover" />
                      )}
                      <div className="p-2 text-xs text-navy/70 line-clamp-2">
                        {m.caption || <span className="italic">(no caption)</span>}
                      </div>
                      <div className="px-2 pb-2 text-[10px] text-navy/40">
                        {m.like_count || 0} likes · {m.comments_count || 0} comments
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value, hint, testid }) => (
  <div className="bg-white rounded-2xl shadow-sm p-4" data-testid={testid}>
    <div className="text-navy/60 text-xs uppercase font-semibold tracking-wider flex items-center gap-1">
      {icon} {label}
    </div>
    <div className="text-2xl font-bold text-navy mt-1">{value}</div>
    {hint && <div className="text-navy/50 text-xs mt-0.5">{hint}</div>}
  </div>
);

const FunnelStep = ({ n, label, value, caption, accent, testid }) => (
  <div
    className={`rounded-xl p-4 ${accent ? 'bg-pink-600 text-white' : 'bg-cream'}`}
    data-testid={testid}
  >
    <div className={`text-xs font-semibold uppercase tracking-wider ${accent ? 'text-white/70' : 'text-navy/50'}`}>
      Step {n} · {label}
    </div>
    <div className={`text-3xl font-bold mt-1 ${accent ? 'text-white' : 'text-navy'}`}>{value}</div>
    <div className={`text-xs mt-1 ${accent ? 'text-white/80' : 'text-navy/60'}`}>{caption}</div>
  </div>
);

// Downloadable trackable QR code for the wrap. Encodes {backend}/api/b/{aid}
// which 302-redirects to the pinned post (or profile) and logs the scan.
const BloomWrapQR = ({ advertiser_id, ig_username, multi, prize }) => {
  // With two brands on one wrap each side needs its own QR, so the handle is
  // part of the scan URL.
  const url = multi && ig_username
    ? `${process.env.REACT_APP_BACKEND_URL}/api/b/${advertiser_id}/${ig_username}`
    : `${process.env.REACT_APP_BACKEND_URL}/api/b/${advertiser_id}`;
  const downloadPNG = () => {
    // Grab the rendered SVG and rasterize it to PNG on a canvas
    const svg = document.querySelector('[data-testid="bloom-wrap-qr-svg"]');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const size = 1024;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const svg64 = btoa(unescape(encodeURIComponent(svgData)));
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const a = document.createElement('a');
      a.download = `advecti-bloom-qr-${ig_username || advertiser_id}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + svg64;
  };
  return (
    <div className="bg-gradient-to-br from-pink-50 to-white rounded-2xl border border-pink-200 p-6 mb-6" data-testid="bloom-wrap-qr-card">
      <div className="flex items-start gap-6 flex-wrap">
        <div className="bg-white p-3 rounded-xl shadow-sm">
          <QRCodeSVG
            value={url}
            size={160}
            level="H"
            includeMargin={false}
            data-testid="bloom-wrap-qr-svg"
          />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-2">
            <QrCode className="text-pink-600" size={20} />
            <h3 className="font-bold text-navy">Your Bloom wrap QR code</h3>
          </div>
          <p className="text-navy/70 text-sm mb-3">
            Print this on your wrap next to your @handle. Every scan opens your
            <strong> pinned Instagram post</strong> directly — one tap and they can
            follow, comment, and enter {prize ? <strong>{prize}</strong> : 'the monthly giveaway'}.
          </p>
          <ul className="text-navy/60 text-xs space-y-1 mb-4">
            <li>• Scans are logged automatically for attribution.</li>
            <li>• Destination updates whenever you change your pinned post — no reprint needed.</li>
            <li>• Uses error-correction level H so small wrap smudges still scan.</li>
          </ul>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={downloadPNG}
              className="px-4 py-2 bg-pink-600 text-white rounded-lg text-sm font-semibold hover:bg-pink-700 inline-flex items-center gap-1"
              data-testid="bloom-wrap-qr-download-btn"
            >
              <Download size={14} /> Download PNG (1024×1024)
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 text-navy/70 hover:bg-white rounded-lg text-sm inline-flex items-center gap-1"
              data-testid="bloom-wrap-qr-test-link"
            >
              <ExternalLink size={14} /> Test scan
            </a>
          </div>
          <p className="text-navy/40 text-xs mt-3 break-all">
            Encoded URL: <code>{url}</code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BloomAttributionTab;
